import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import Grid from '@mui/material/Grid2';
import { BoxCenterStart } from '../style/box';
import { TypographyTitle } from '../style/typography';
import { Chip } from "@mui/material";
import { commonStyles } from "../style/common";
import EarthquakeInfo from "../components/earthquake_info";
import ContactInfo from "../components/contact_info";
import DamCompatibilityStatus from "../components/dam_compatibility_status";
import PowerplantCompatibilityStatus from "../components/powerplant_compatibility_status";
import ReportStopDialog from "../components/report_stop_dialog";
import { fetchData } from "../func/api";
import SkeletonLoading from "../components/skeleton_loading";
import { ROLE_UUID, REPORT_STATUS } from "../const/config";
import { useAuth } from "../contexts/AuthContext";

const EarthQuakeDetail = () => {
    const [earthquake, setEarthquake] = useState([]);
    const [damWorkAssignList, setDamWorkAssignList] = useState([]);
    const [powerplantWorkAssignList, setPowerplantWorkAssignList] = useState([]);
    const [damReportList, setDamReportList] = useState([]);
    const [damReportDetailList, setDamReportDetailList] = useState([]);
    const [powerplantReportDetailList, setPowerplantReportDetailList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState("first");

    const { uuid } = useParams();
    const { currentUser } = useAuth();

    const isButtonDisabled =
        earthquake?.report_status_uuid === REPORT_STATUS.REPORTSTOP
        || (earthquake?.report_status_uuid === REPORT_STATUS.SECONDARYCHECK
            && earthquake?.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTCOMPLETE)
        || currentUser?.role_id === ROLE_UUID.VIEWER;

    const fetchDataAndUpdateState = useCallback(async () => {
        if (!uuid) return;

        try {
            // 地震情報、ダムまたは発電所業務担当者一覧、ダムまたは発電所対応状況一覧を並行して取得
            const earthquake = await fetchData(`api/get_earthquake/${uuid}`);
            const reportUuid = earthquake?.report_uuid || '';
            const [damAssignList, powerplantAssignList, damList, damDetailList, powerplantDetailList] = await Promise.all([
                fetchData(`api/get_work_contact_list/${uuid}/1`),
                fetchData(`api/get_work_contact_list/${uuid}/2`),
                fetchData(`api/get_dam_report_list/${uuid}`),
                fetchData(`api/get_dam_report_detail_list/${uuid}`),
                fetchData(`api/get_powerplant_report_detail_list/${reportUuid}`)
            ]);

            setEarthquake(earthquake);
            setDamWorkAssignList(damAssignList);
            setPowerplantWorkAssignList(powerplantAssignList);
            setDamReportList(damList);
            setDamReportDetailList(damDetailList);
            setPowerplantReportDetailList(powerplantDetailList);
        } catch (error) {
            console.error("データ取得中にエラーが発生しました:", error);
        }
    }, [
        uuid,
        setEarthquake, setDamWorkAssignList, setPowerplantWorkAssignList, setDamReportList, setDamReportDetailList, setPowerplantReportDetailList
    ]);

    useEffect(() => {
        if (!uuid) return;

        // 1. 初回ロード時のデータ取得（ローディング状態の管理を含む）
        const initialFetch = async () => {
            setLoading(true);
            try {
                await fetchDataAndUpdateState();
            } catch (error) {
                // fetchDataAndUpdateState 内でエラー処理済み
            } finally {
                setLoading(false);
            }
        };

        initialFetch();

        // 2. 30秒ごとの定期実行を設定
        const THIRTY_SECONDS = 0.1 * 60 * 1000;
        const intervalId = setInterval(() => {
            // 定期実行時は loading state の更新は行わない
            fetchDataAndUpdateState();
        }, THIRTY_SECONDS);
        return () => {
            clearInterval(intervalId);
        };
    }, [uuid, fetchDataAndUpdateState]);

    // 「招集/業務割当」ボタン押下時
    const handleAssignClick = () => {
        // 業務対応可否確認画面に遷移
        window.location.href = `/callout/${uuid}`;
    };

    // 「報告作業を開始する」ボタン押下時
    const handleReportClick = (reportDetailList, type) => {
        // 現在の業務ステップを判定する関数
        const getCurrentStep = (reportDetailList, type) => {
            if (type === "dam") {
                if (!reportDetailList?.ft_reported_to_internal_at) {
                    // ダムの初動対応の報告業務画面に遷移
                    return window.location.href = `/initial_task/${uuid}`;
                }
                else if (!reportDetailList?.ir_reported_to_internal_at && (!earthquake.occured_at || !earthquake.epicenter || !earthquake.max_intensity)) {
                    // ダムのCSVファイルアップロード画面に遷移
                    return window.location.href = `/upload_csvfile/${uuid}`;
                }
                else if (!reportDetailList?.ir_reported_to_internal_at) {
                    // ダムの第1報の報告業務画面に遷移
                    return window.location.href = `/first_report/${uuid}`;
                }
                else if (!reportDetailList?.pi_reported_to_internal_at) {
                    // ダムの1次点検の報告業務画面に遷移
                    return window.location.href = `/primary_inspection/${uuid}`;
                }
                else if (!reportDetailList?.si_reported_to_internal_at) {
                    // ダムの2次点検の報告業務画面に遷移
                    return window.location.href = `/secondary_inspection/${uuid}`;
                }
            } else if (type === "powerplant") {
                return window.location.href = `/powerplant_work_check/${uuid}`;
            }
        };
        getCurrentStep(reportDetailList, type);
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid container spacing={7} columns={12} sx={commonStyles.container}>
                {/* 業務ステータス */}
                <Grid size={{ xs: 12 }}>
                    <Chip
                        label={
                            earthquake?.report_status_name === "途中終了"
                                ? "全業務：途中終了"
                                : (earthquake?.report_status_name === "２次点検報告済"
                                    && earthquake?.report_powerplant_status_name === "発電所対応完了")
                                    ? "全業務：完了"
                                    : "全業務：対応中"
                        }
                        color="primary"
                        sx={{
                            fontWeight: 'bold',
                            color: '#fff',
                            backgroundColor:
                                earthquake?.report_status_name === "途中終了"
                                    ? 'rgb(189,189,189)'
                                    : (earthquake?.report_status_name === "２次点検報告済"
                                        && earthquake?.report_powerplant_status_name === "発電所対応完了")
                                        ? 'rgb(72,161,75)'
                                        : 'rgb(53,121,188)'
                        }}
                    />
                </Grid>
                {/* 地震タイトル */}
                <Grid size={{ xs: 12 }}>
                    {!isButtonDisabled && (
                        <ReportStopDialog initial_report_uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} />
                    )}
                    <TypographyTitle>{earthquake.name}</TypographyTitle>
                </Grid>
                {/* 地震概要 */}
                <Grid size={{ xs: 12 }}>
                    <EarthquakeInfo earthquake={earthquake} />
                </Grid>
                {/* 担当者情報 */}
                <Grid size={{ xs: 12 }}>
                    <ContactInfo
                        onAssignClick={handleAssignClick}
                        damWorkAssignList={damWorkAssignList}
                        powerplantWorkAssignList={powerplantWorkAssignList}
                            isButtonDisabled={isButtonDisabled}
                    />
                </Grid>
                <Grid container size={{ xs: 12 }} spacing={2}>
                    {/* ダム対応状況 */}
                    <DamCompatibilityStatus
                        title={"ダム対応状況"}
                        earthquake={earthquake}
                        onReportClick={handleReportClick}
                        type={"dam"}
                        reportList={damReportList}
                        reportDetailList={damReportDetailList}
                        selectedItem={selectedItem}
                        setSelectedItem={setSelectedItem}
                        isButtonDisabled={isButtonDisabled}
                    />
                    {/* 発電所対応状況 */}
                    <PowerplantCompatibilityStatus
                        title={"発電所対応状況"}
                        onReportClick={handleReportClick}
                        type={"powerplant"}
                        reportDetailList={powerplantReportDetailList}
                        isButtonDisabled={isButtonDisabled}
                    />
                </Grid>
            </Grid>
        </BoxCenterStart>
    );
};

export default EarthQuakeDetail;